import axios from 'axios';

export const PORT_HUB_TYPES = [
  'user-favorite',
  'user-custom-model',
  'managed-cio',
  'user-model',
];

// ACTION TYPES
const name = 'data';
export const actionTypes = {
  LOAD_SAMPLE_DATA: `${name}:load_sample_data`,
  LOAD_PERSONAL_STRATEGIES: `${name}:'get_personal_strategies`,
  DELETE_A_STRATEGY: `${name}:'delete_a_stategy`,
  SHOW_A_STRATEGY: `${name}:'show_a_stategy`,
  SHOW_ALL_STRATEGIES: `${name}:'show_all_stategies`,
  SHOW_STRATEGY_EDITOR: `${name}:'show_strategy_editor`,
  CLOSE_STRATEGY_EDITOR: `${name}:'close_strategy_editor`,
  UPDATE_STRATEGY: `${name}:'update_strategy`,
  LOAD_TYPEAHEAD_SUGGESTIONS: `${name}:'load_typeahead_suggestions`,
  SAVING_NEW_STRATEGY_IN_PROGRESS: `${name}:'saving_new_strategy_in_progress`,
  SAVING_STRATEGY_IN_PROGRESS: `${name}:'saving_strategy_in_progress`,
  SAVING_STRATEGY_COMPLETE: `${name}:'saving_strategy_complete`,
  SAVE_NEW_STRATEGY: `${name}:'save_new_strategy`,
  LOAD_BENCHMARKS: `${name}:'load_benchmarks`,
  LOAD_PERSONAL_STRATEGIES_ERROR: `${name}:load_personal_strategies_error`,
  RESET_PERSONAL_STRATEGIES_STATE: `${name}:reset_personal_strategies_state`,
  LOAD_PERSONAL_STRATEGIES_SECTION_START: `${name}:load_personal_strategies_section_start`,
  LOAD_PERSONAL_STRATEGIES_SECTION_SUCCESS: `${name}:load_personal_strategies_section_success`,
  LOAD_PERSONAL_STRATEGIES_SECTION_ERROR: `${name}:load_personal_strategies_section_error`,
};

// Load all personal strategies for a user
export function loadPersonalStrategies() {
  return (dispatch, getState) => {
    const params = getState().params;

    // Always start with a clean slate
    dispatch({ type: actionTypes.RESET_PERSONAL_STRATEGIES_STATE });

    // If no token, mark every section as an empty success and exit early
    if (!params?.token) {
      PORT_HUB_TYPES.forEach((portHubType) => {
        dispatch({
          type: actionTypes.LOAD_PERSONAL_STRATEGIES_SECTION_SUCCESS,
          portHubType,
          value: [],
        });
      });
      return Promise.resolve();
    }

    const requests = PORT_HUB_TYPES.map((portHubType) => {
      dispatch({
        type: actionTypes.LOAD_PERSONAL_STRATEGIES_SECTION_START,
        portHubType,
      });

      const request = {
        method: 'GET',
        url: `${process.env.REACT_APP_API_URL}/personalstrategies/list-v2/`,
        params: { clientEmail: params.clientEmail, portHubType },
        headers: { Authorization: `Token ${params.token}` },
      };

      return axios(request)
        .then((response) => {
          if (response.status !== 200) {
            throw new Error(response.statusText || response.status);
          }

          const sortedData = Array.isArray(response.data)
            ? [...response.data].sort((a, b) =>
                new Date(a.updateDate) >= new Date(b.updateDate) ? -1 : 1
              )
            : [];

          // Debug: surface what came back for this tab (full objects)
          // console.log('loadPersonalStrategies', {
          //   portHubType,
          //   count: sortedData.length,
          //   sample: sortedData.slice(0, 10),
          // });

          dispatch({
            type: actionTypes.LOAD_PERSONAL_STRATEGIES_SECTION_SUCCESS,
            portHubType,
            value: sortedData,
          });
        })
        .catch((error) => {
          console.error(
            'Failed to load personal strategies:',
            portHubType,
            error
          );
          dispatch({
            type: actionTypes.LOAD_PERSONAL_STRATEGIES_SECTION_ERROR,
            portHubType,
            error: error?.message || 'Error loading portfolios.',
          });
        });
    });

    return Promise.allSettled(requests);
  };
}

export function deletePersonalStrategy(idToDelete) {
  return (dispatch, getState) => {
    // Let the StrategyList know that one of the strategies is being deleted
    dispatch({
      type: actionTypes.SAVING_STRATEGY_IN_PROGRESS,
      value: idToDelete,
    });
    dispatch({ type: actionTypes.DELETE_A_STRATEGY, value: idToDelete });

    let params = getState().params;

    const body = {
      clientEmail: params.clientEmail,
      isDeleted: true,
    };

    if (params.token !== 'undefined' && idToDelete) {
      const request = {
        method: 'PUT',
        url: `${process.env.REACT_APP_API_URL}/personalstrategies/${idToDelete}/`,
        data: body,
        headers: {
          'Content-Type': 'application/json',
          Authorization: params.token && `Token ${params.token}`,
        },
      };
      return axios(request)
        .then((response) => {
          dispatch({ type: actionTypes.DELETE_A_STRATEGY, value: idToDelete });
          dispatch({ type: actionTypes.SHOW_ALL_STRATEGIES });
          dispatch({ type: actionTypes.SAVING_STRATEGY_COMPLETE });
        })
        .catch((error) => {
          console.error(error);
        });
    }
  };
}

// Show a given personal strategy by id, and hide all the rest in the StrategyList
export function showPersonalStrategy(idToShow) {
  return async (dispatch, getState) => {
    // Dispatch the action to show the strategy
    dispatch({ type: actionTypes.SHOW_A_STRATEGY, value: idToShow });

    // Update the last viewed timestamp
    try {
      const params = getState().params;

      if (params.token) {
        await axios.put(
          `${process.env.REACT_APP_API_URL}/personalstrategies/${idToShow}/update_last_viewed/`,
          { clientEmail: params.clientEmail },
          { headers: { Authorization: `Token ${params.token}` } }
        );
      }
    } catch (error) {
      console.error('Error updating last viewed time:', error);
    }
  };
}

export function updatePersonalStrategyName(idToShow, newName) {
  return async (dispatch, getState) => {
    // Update the name
    try {
      const params = getState().params;

      if (params.token) {
        await axios.put(
          `${process.env.REACT_APP_API_URL}/personalstrategies/${idToShow}/update_name/`,
          { clientEmail: params.clientEmail, name: newName },
          { headers: { Authorization: `Token ${params.token}` } }
        );
      }
    } catch (error) {
      console.error('Error updating personal strategy name:', error);
    }
  };
}

// Show all strategies in the StrategyList
export function showAllStrategies() {
  return (dispatch, getState) => {
    dispatch({ type: actionTypes.SHOW_ALL_STRATEGIES });
  };
}

// Show the Strategy editor. May be called from the StrategyList
// or an individual StrategyRow
export function showStrategyEditor(personalStrategy) {
  return (dispatch, getState) => {
    dispatch({
      type: actionTypes.SHOW_STRATEGY_EDITOR,
      value: personalStrategy,
    });
  };
}

// Close the StrategyEditor
export function closeStrategyEditor() {
  return (dispatch, getState) => {
    dispatch({ type: actionTypes.CLOSE_STRATEGY_EDITOR });
  };
}

// Update the Personal Strategy's details (e.g., name)
export function saveNewStrategy(fieldsToUpdate) {
  return (dispatch, getState) => {
    // Let the StrategyList know that a new strategy is being saved
    dispatch({
      type: actionTypes.SAVING_NEW_STRATEGY_IN_PROGRESS,
      value: fieldsToUpdate,
    });

    let params = getState().params;

    let body = {
      clientEmail: params.clientEmail,
      wtRegion: params.wtRegion,
      name: fieldsToUpdate.name,
      holdings: fieldsToUpdate.holdings,
      totalValue: fieldsToUpdate.totalValue,
    };

    // Add a benchmark if one has been added
    if (fieldsToUpdate.benchmarkExternalID) {
      body.benchmarks = [
        {
          externalID: fieldsToUpdate.benchmarkExternalID,
        },
      ];
    }

    if (params.token !== 'undefined') {
      const request = {
        method: 'POST',
        url: `${process.env.REACT_APP_API_URL}/personalstrategies/`,
        data: body,
        headers: {
          'Content-Type': 'application/json',
          Authorization: params.token && `Token ${params.token}`,
        },
      };
      return axios(request)
        .then((response) => {
          dispatch({
            type: actionTypes.SAVE_NEW_STRATEGY,
            value: response.data,
          });
          dispatch({ type: actionTypes.SAVING_STRATEGY_COMPLETE });

          // Update the Hash URL to force dpd to reload
          window.location = '#myportfolios&reloadhistory=true';

          // Optimize the strategy now it's been created
          optimizePersonalStrategy(dispatch, params, response.data.id);
        })
        .catch((error) => {
          console.error(error);
        });
    }
  };
}

// Update the Personal Strategy's details (e.g., name)
export function updateStategyDetails(fieldsToUpdate) {
  return (dispatch, getState) => {
    // Let the StrategyList know that one of the strategies is being saved
    dispatch({
      type: actionTypes.SAVING_STRATEGY_IN_PROGRESS,
      value: fieldsToUpdate.id,
    });

    let params = getState().params;

    let body = {
      clientEmail: params.clientEmail,
      name: fieldsToUpdate.name,
      totalValue: fieldsToUpdate.totalValue,
    };

    // Check if holdings were updated
    if (fieldsToUpdate.holdings) {
      body.holdings = fieldsToUpdate.holdings;
    }

    // Add a benchmark if one has been added, otherwise force it to be blanked out with empty array
    if (fieldsToUpdate.benchmarkExternalID) {
      body.benchmarks = [
        {
          externalID: fieldsToUpdate.benchmarkExternalID,
        },
      ];
    } else {
      body.benchmarks = [];
    }

    if (params.token !== 'undefined' && fieldsToUpdate.id) {
      const request = {
        method: 'PUT',
        url: `${process.env.REACT_APP_API_URL}/personalstrategies/${fieldsToUpdate.id}/`,
        data: body,
        headers: {
          'Content-Type': 'application/json',
          Authorization: params.token && `Token ${params.token}`,
        },
      };
      return axios(request)
        .then((response) => {
          dispatch({ type: actionTypes.UPDATE_STRATEGY, value: response.data });
          dispatch({ type: actionTypes.SAVING_STRATEGY_COMPLETE });

          // Update the Hash URL to force dpd to reload
          window.location = '#myportfolios&reloadhistory=true';

          // Check if holdings were updated
          if (fieldsToUpdate.holdings) {
            // Optimize the strategy now it's been created
            optimizePersonalStrategy(dispatch, params, fieldsToUpdate.id);
          }
        })
        .catch((error) => {
          console.error(error);
        });
    }
  };
}

// Send a strategy to DPD to optimize (not designed to be used outside of an existing action)
function optimizePersonalStrategy(dispatch, params, idToOptimize) {
  const body = {
    clientEmail: params.clientEmail,
  };

  if (params.token !== 'undefined' && idToOptimize) {
    const request = {
      method: 'POST',
      url: `${process.env.REACT_APP_API_URL}/personalstrategies/${idToOptimize}/optimize/`,
      data: body,
      headers: {
        'Content-Type': 'application/json',
        Authorization: params.token && `Token ${params.token}`,
      },
    };
    return axios(request)
      .then((response) => {
        dispatch({ type: actionTypes.UPDATE_STRATEGY, value: response.data });
      })
      .catch((error) => {
        console.error(error);
      });
  }
}

// Create a new strategy out of a portfolio from DPD
export function savePortfolioAsNewStrategy(portfolioId, portfolioName) {
  return (dispatch, getState) => {
    let params = getState().params;

    let body = {
      clientEmail: params.clientEmail,
      portfolioId: portfolioId,
      portfolioName: portfolioName,
    };

    if (params.token !== 'undefined' && portfolioId) {
      const request = {
        method: 'POST',
        url: `${process.env.REACT_APP_API_URL}/personalstrategies/create_from_portfolio/`,
        data: body,
        headers: {
          'Content-Type': 'application/json',
          Authorization: params.token && `Token ${params.token}`,
        },
      };
      return axios(request)
        .then((response) => {
          dispatch({
            type: actionTypes.SAVE_NEW_STRATEGY,
            value: response.data,
          });

          // Update the Hash URL to let DPD know saving has finished
          window.location =
            '#dpd&savedToMyPortfolios=' +
            portfolioId +
            '&newMyPortfolio=' +
            response.data.id;
        })
        .catch((error) => {
          console.error(error);
        });
    }
  };
}

/// Get all the benchmarks from the database
export function getBenchmarks() {
  return (dispatch, getState) => {
    let params = getState().params;

    if (params.token !== undefined) {
      const request = {
        method: 'GET',
        url: `${process.env.REACT_APP_DPD_URL}/api/benchmarks/all/`,
        params: {
          clientEmail: params.clientEmail,
          entityCCY: params.entityCCY,
          wtRegion: params.wtRegion,
        },
        headers: {
          Authorization: params.token && `Token ${params.token}`,
        },
      };
      return axios(request)
        .then((response) => {
          if (response.status === 200) {
            dispatch({
              type: actionTypes.LOAD_BENCHMARKS,
              value: response.data,
            });
          }
        })
        .catch((error) => {
          console.error(error);
        });
    }
  };
}

// When a user creates a new strategy, we add on to the existing and update
export function loadPersonalStrategiesFromNew(value) {
  return (dispatch) => {
    dispatch({ type: actionTypes.LOAD_PERSONAL_STRATEGIES, value: value });
  };
}

// When a user updated an exsisting strategy, we add on to the existing and update
export function updatePersonalStrategiesFromNew(value) {
  return (dispatch) => {
    dispatch({ type: actionTypes.UPDATE_STRATEGY, value: value });
  };
}
